import React, { useState, useEffect } from 'react';

const Logo = () => (
  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" style={{width:'100%',height:'100%',display:'block'}}>
    <defs>
      <linearGradient id="lg-land" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F0CC6E"/>
        <stop offset="50%" stopColor="#C9973A"/>
        <stop offset="100%" stopColor="#8B6020"/>
      </linearGradient>
    </defs>
    <circle cx="60" cy="60" r="58" fill="#0a0a12" stroke="url(#lg-land)" strokeWidth="1.5"/>
    <ellipse cx="60" cy="72" rx="28" ry="22" fill="none" stroke="url(#lg-land)" strokeWidth="1.8"/>
    <circle cx="60" cy="38" r="14" fill="none" stroke="url(#lg-land)" strokeWidth="1.8"/>
    <circle cx="60" cy="34" r="4" fill="#C9973A"/>
    <path d="M46 60 Q38 55 36 68 Q34 80 60 85 Q86 80 84 68 Q82 55 74 60" fill="none" stroke="url(#lg-land)" strokeWidth="1.8"/>
    <line x1="48" y1="72" x2="72" y2="72" stroke="url(#lg-land)" strokeWidth="1.2"/>
    <line x1="50" y1="77" x2="70" y2="77" stroke="url(#lg-land)" strokeWidth="1"/>
    <path d="M30 55 L20 45 L38 50" fill="none" stroke="#E2B84A" strokeWidth="1.5"/>
    <path d="M90 55 L100 45 L82 50" fill="none" stroke="#E2B84A" strokeWidth="1.5"/>
    <circle cx="60" cy="90" r="8" fill="none" stroke="url(#lg-land)" strokeWidth="1.5"/>
    <text x="60" y="94" textAnchor="middle" fill="#C9973A" fontSize="9" fontWeight="bold">₿</text>
    <text x="22" y="42" textAnchor="middle" fill="#C9973A" fontSize="8" fontWeight="bold">₿</text>
    <text x="98" y="42" textAnchor="middle" fill="#C9973A" fontSize="7" fontWeight="bold">Ξ</text>
  </svg>
);

function isMarketClosed() {
  const now = new Date();
  const day = now.getUTCDay();
  const h = now.getUTCHours();
  const m = now.getUTCMinutes();
  const t = h * 60 + m;
  if (day === 6) return true;
  if (day === 5 && t >= 22 * 60) return true;
  if (day === 0 && t < 22 * 60) return true;
  return false;
}

function getSessionStatus() {
  const now = new Date();
  const utcTotal = now.getUTCHours() * 60 + now.getUTCMinutes();
  const closed = isMarketClosed();
  return [
    { name: 'Asia', start: 0, end: 540 },
    { name: 'London', start: 480, end: 990 },
    { name: 'NY Overlap', start: 780, end: 990 },
    { name: 'New York', start: 780, end: 1260 },
  ].map(s => ({ ...s, active: !closed && utcTotal >= s.start && utcTotal < s.end, marketClosed: closed }));
}

function LiveSessionBar() {
  const [sessions, setSessions] = useState(getSessionStatus());
  const [utc, setUtc] = useState('');
  useEffect(() => {
    const tick = () => {
      setSessions(getSessionStatus());
      setUtc(new Date().toUTCString().slice(17, 25) + ' UTC');
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="land-session-bar">
      <span>{utc}</span>
      {sessions[0]?.marketClosed && (
        <span className="land-session-item" style={{color:'var(--amber)'}}>
          <span className="session-dot" style={{background:'var(--amber)'}}></span>
          Market Closed
        </span>
      )}
      {sessions.map(s => (
        <span key={s.name} className="land-session-item">
          <span className={`session-dot ${s.active ? 'active' : ''}`}></span>
          {s.name}
        </span>
      ))}
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className={`faq-chevron ${open ? 'open' : ''}`}>▼</span>
      </button>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
}

export default function LandingPage({ onSignIn }) {
  function handleSubscribe() {
    // Anyone clicking subscribe needs to sign in first.
    // After signin they'll land on the app and can subscribe from Billing page.
    onSignIn();
  }

  return (
    <div className="landing">
      {/* NAV */}
      <nav className="land-nav">
        <div className="land-nav-brand">
          <div style={{width:36,height:36,overflow:'hidden',flexShrink:0}}><Logo /></div>
          <span className="land-nav-name">Trading Buddha</span>
        </div>
        <div className="land-nav-right">
          <a href="#features" className="land-nav-link">Features</a>
          <a href="#pricing" className="land-nav-link">Pricing</a>
          <button className="btn-ghost" style={{padding:'8px 18px',fontSize:'13px'}} onClick={onSignIn}>Sign In</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="land-hero">
        <div className="land-hero-content">
          <div className="land-hero-logo"><Logo /></div>
          <div className="land-hero-eyebrow">XAUUSD Pro Framework · Institutional Grade · AI-Powered</div>
          <h1 className="land-hero-h1">The Gold Standard<br /><em>Trading Playbook</em></h1>
          <p className="land-hero-sub">
            A precision XAUUSD framework built on institutional liquidity concepts.
            Your private trade journal, AI trading coach, position calculator, and full strategy guide — in one platform.
          </p>
          <LiveSessionBar />
          <div className="land-hero-ctas">
            <button className="btn-gold" onClick={handleSubscribe} style={{fontSize:18,padding:'14px 36px'}}>
              Subscribe — ₹999/month
            </button>
            <a href="#features" className="btn-ghost">See What's Inside</a>
          </div>
          <div className="land-pills">
            {['AI Trading Coach','47–53% Win Rate','1:2.5+ Risk/Reward','Cancel Anytime','1% Max Risk','Monthly Subscription'].map(p => (
              <span key={p} className="land-pill">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <div className="stats-bar">
        {[
          {val:'AI-Powered', label:'Coach Built-In'},
          {val:'$100–200', label:'Daily Gold Range'},
          {val:'4H / 1H / 15M', label:'Multi-TF Framework'},
          {val:'London + NY', label:'Primary Sessions'},
          {val:'Risk-First', label:'Core Approach'},
        ].map(s => (
          <div key={s.label} className="stat-bar-item">
            <div className="stat-bar-val">{s.val}</div>
            <div className="stat-bar-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* WHAT YOU GET */}
      <section className="land-section" id="features">
        <h2 className="land-section-title">Everything You <em>Need to Trade Gold</em></h2>
        <p className="land-section-sub">One platform. Your data stays private. Cancel anytime.</p>
        <div className="features-grid">
          {[
            {icon:'✦', title:'AI Trading Coach', desc:"Get instant AI coaching on every trade. Validate setups before you enter. Daily performance reviews. ."},
            {icon:'📓', title:'Private Trade Journal', desc:"Log every XAUUSD trade. Your data is completely isolated — no other user can see your journal. Full stats, export to CSV."},
            {icon:'🧮', title:'Position Size Calculator', desc:"Enter your balance, risk %, and stop distance. Instantly get exact lot size, dollar risk, and target profit."},
            {icon:'🎲', title:'Monte Carlo Simulator', desc:"Run 1,000 simulated trade sequences. See your real risk of ruin, expected drawdowns, and probability of profit."},
            {icon:'🛡️', title:'Risk Manager', desc:"Daily loss limits, weekly limits, consecutive loss alerts. The app stops you before you violate your rules."},
            {icon:'📊', title:'Deep Analytics', desc:"Win rate by session, setup, day of week. Equity curve. Profit factor. Checklist correlation. Streak tracking."},
            {icon:'✅', title:'Pre-Trade Checklist', desc:"12-point interactive checklist validates every entry. Build discipline by confirming all conditions before risking capital."},
            {icon:'📖', title:'Full Strategy Playbook', desc:"5-condition institutional entry framework, stop placement logic, TP structure (50%/40%/10%), and session timing."},
            {icon:'🏆', title:'Achievements & Streaks', desc:"22 badges across 5 tiers. Gamified discipline tracking. Stay consistent with daily streaks."},
          ].map(f => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-title">{f.title}</div>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STRATEGY PREVIEW */}
      <section style={{background:'var(--bg-1)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'80px 40px'}}>
        <div style={{maxWidth:700, margin:'0 auto'}}>
          <h2 className="land-section-title">The 5-Condition <em>Entry Framework</em></h2>
          <p className="land-section-sub">Every trade must satisfy all 5 conditions. No exceptions.</p>
          <div className="strategy-preview">
            {[
              {title:'4H Structural Bias', desc:'Define the dominant trend and premium/discount zones on the 4-hour chart before looking for any entry.', locked: false},
              {title:'Liquidity Level Identified', desc:'Mark the nearest significant liquidity pool — equal highs/lows, previous session highs/lows — that price is drawn to.', locked: false},
              {title:'[Unlocked with subscription]', desc:'Third entry condition is revealed inside the platform.', locked: true},
              {title:'[Unlocked with subscription]', desc:'Fourth entry condition is revealed inside the platform.', locked: true},
              {title:'[Unlocked with subscription]', desc:'Fifth entry condition is revealed inside the platform.', locked: true},
            ].map((s, i) => (
              <div key={i} className={`strategy-item ${s.locked ? 'strategy-blur' : ''}`}>
                <div className="strategy-num">{i+1}</div>
                <div className="strategy-text">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
            <div className="strategy-cta-wrap">
              <button className="btn-gold" onClick={handleSubscribe}>
                Subscribe to Unlock All 5 Conditions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="land-section" id="pricing">
        <h2 className="land-section-title">Simple, <em>Transparent Pricing</em></h2>
        <p className="land-section-sub">One subscription. Full access. Cancel anytime.</p>
        <div className="pricing-wrap">
          <div className="pricing-card">
            <div className="pricing-badge">MONTHLY SUBSCRIPTION</div>
            <div className="pricing-plan-name">FULL PLATFORM</div>
            <div className="pricing-price">₹999<span style={{fontSize:18,color:'var(--text-3)',fontFamily:'Mulish,sans-serif'}}> /month</span></div>
            <div className="pricing-price-note">Billed monthly · Cancel anytime · Instant access</div>
            <ul className="pricing-features">
              {[
                'AI Trading Coach',
                'Full XAUUSD strategy playbook (5-condition framework)',
                'Private trade journal (per-user, isolated)',
                'Position size calculator with risk-of-ruin model',
                'Monte Carlo simulator',
                'Daily bias planner + risk manager',
                'Deep performance analytics',
                '22 achievement badges + streak tracking',
                'All future feature updates included',
              ].map(f => <li key={f} className="pricing-feature"><span className="pricing-feature-check">✓</span> {f}</li>)}
            </ul>
            <div className="pricing-btn-wrap">
              <button className="btn-gold" onClick={handleSubscribe}>
                Get Started Now
              </button>
            </div>
            <div className="pricing-secure">🔒 Secure payment via Razorpay · UPI, Cards, Net Banking</div>
            <div className="pricing-refund">Cancel anytime in one click from the billing page.</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="land-section">
        <h2 className="land-section-title">Frequently Asked <em>Questions</em></h2>
        <div className="faq-list">
          <FAQ q="How does the subscription work?" a="It's a simple ₹999/month subscription. You create an account, subscribe via Razorpay using UPI / card / netbanking, and get instant access to the full platform. You're auto-billed every month — cancel anytime in one click from the billing page." />
          <FAQ q="What payment methods do you accept?" a="We accept UPI (GPay, PhonePe, Paytm, BHIM), credit/debit cards (Visa, Mastercard, RuPay, Amex), and all major Indian banks via netbanking — powered by Razorpay, India's leading payment gateway. No card details are ever stored on our servers." />
          <FAQ q="Can I cancel anytime?" a="Yes. Go to the Billing page in your dashboard and click 'Manage Subscription'. Cancel with one click. You'll keep access until the end of your current paid month, then your subscription ends. No hidden cancellation fees." />
          <FAQ q="Is this a signal service?" a="No. Trading Buddha is a framework, AI coach, and private trade journal — not a signal service. You learn the strategy and execute independently. No one tells you when to trade." />
          <FAQ q="What markets does this cover?" a="The strategy is built specifically for XAUUSD (gold spot). The concepts — liquidity, structure, sessions — apply broadly, but every example and tool is calibrated for gold." />
          <FAQ q="How does the AI Trading Coach work?" a="The AI Coach has three modes: Setup Validator rates your trade idea before you enter (1-10 score with TAKE/SKIP/WAIT verdict), Trade Review analyzes any logged trade and gives coaching feedback, and Performance Review analyzes your last 3-30 days as a whole. Each user gets a daily allowance of AI requests." />
          <FAQ q="Is my trade journal private?" a="Yes — completely. Each user's trades are isolated at the database level using row-level security. Not even the admin can see your trade data through the journal interface." />
          <FAQ q="What if I want a refund?" a="If you're not satisfied within the first 7 days of your first month, email us and we'll process a full refund. After that, you can cancel anytime to stop future billing." />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="land-footer">
        <div className="land-footer-brand">
          <div style={{width:28,height:28,overflow:'hidden',flexShrink:0}}><Logo /></div>
          <span className="land-footer-name">Trading Buddha</span>
        </div>
        <p className="land-footer-disclaimer">
          Not financial advice. Trading involves substantial risk of loss and is not suitable for all investors.
          Past performance is not indicative of future results.
        </p>
        <div className="land-footer-links">
          <a href="#privacy">Privacy Policy</a>
          <a href="#terms">Terms of Service</a>
          <button className="land-nav-link" onClick={onSignIn}>Sign In</button>
        </div>
      </footer>
    </div>
  );
}
